import { useState, useEffect, useRef } from "react";
import { ChevronDown, Star, MapPin } from "lucide-react";

const KROGER_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kroger-prices`;
const FAV_STORE_KEY = "kin_kroger_fav_store";

export interface StoreInfo {
  id: string;
  name: string;
  distance?: number;
  address?: string;
}

interface KrogerStorePickerProps {
  currentStore: StoreInfo;
  zipCode: string;
  onStoreChange: (store: StoreInfo) => void;
  onZipChange: (zip: string) => void;
}

export default function KrogerStorePicker({
  currentStore,
  zipCode,
  onStoreChange,
  onZipChange,
}: KrogerStorePickerProps) {
  const [open, setOpen] = useState(false);
  const [stores, setStores] = useState<StoreInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingZip, setEditingZip] = useState(false);
  const [newZip, setNewZip] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const favRaw = localStorage.getItem(FAV_STORE_KEY);
  const favStore: StoreInfo | null = favRaw ? JSON.parse(favRaw) : null;
  const isFav = favStore?.id === currentStore.id;

  const toggleFavorite = () => {
    if (isFav) {
      localStorage.removeItem(FAV_STORE_KEY);
    } else {
      localStorage.setItem(FAV_STORE_KEY, JSON.stringify(currentStore));
    }
    // Force re-render
    window.dispatchEvent(new Event("storage"));
  };

  const fetchStores = async (zip: string) => {
    setLoading(true);
    try {
      const resp = await fetch(KROGER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ action: "stores", zipCode: zip }),
      });
      const data = await resp.json();
      if (data.stores) setStores(data.stores);
    } catch {
      // silently fail
    }
    setLoading(false);
  };

  const handleOpenDropdown = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    // If we have a zip, fetch the 5 nearest stores. If not (e.g. user landed
    // here via a saved favorite store and never entered a zip this session),
    // open the inline zip editor so they can supply one.
    if (zipCode && /^\d{5}$/.test(zipCode)) {
      if (stores.length === 0) fetchStores(zipCode);
    } else {
      setEditingZip(true);
    }
  };

  const handleSelectStore = (store: StoreInfo) => {
    setOpen(false);
    onStoreChange(store);
  };

  const handleZipSubmit = () => {
    const zip = newZip.trim();
    if (!/^\d{5}$/.test(zip)) return;
    setEditingZip(false);
    setStores([]);
    onZipChange(zip);
    fetchStores(zip);
  };

  // Close dropdown on outside click (mouse + touch)
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  return (
    <div ref={dropdownRef} className="relative">
      {/* Store name row */}
      <div className="flex items-center gap-1.5 min-w-0">
        <button
          onClick={toggleFavorite}
          className="text-primary-foreground/80 hover:text-primary-foreground transition-colors flex-shrink-0"
          title={isFav ? "Remove default store" : "Set as default store"}
        >
          <Star size={13} fill={isFav ? "currentColor" : "none"} />
        </button>

        <button
          onClick={handleOpenDropdown}
          className="flex items-center gap-1 text-xs font-body font-semibold text-primary-foreground hover:text-white/90 transition-colors min-w-0"
        >
          <span className="truncate">{currentStore.name}</span>
          <ChevronDown size={12} className={`transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
        </button>

        {isFav && (
          <span className="font-body text-primary-foreground/60 whitespace-nowrap flex-shrink-0" style={{ fontSize: "clamp(9px, 2.2vw, 11px)" }}>
            Default store
          </span>
        )}
      </div>

      {/* Dropdown — absolute overlay anchored to the store name button */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 4,
            width: 256,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E8E6F4",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(28, 26, 62, 0.15)",
            zIndex: 50,
            maxHeight: 280,
            overflow: "hidden",
          }}
        >
          {loading ? (
            <div className="px-3 py-4 text-center text-xs font-body" style={{ color: "#54525C" }}>
              Finding nearby stores...
            </div>
          ) : stores.length === 0 ? (
            <div className="px-3 py-3 text-xs font-body" style={{ color: "#54525C" }}>
              {zipCode && /^\d{5}$/.test(zipCode)
                ? "No stores found. Try a different zip below."
                : "Enter a zip code to find nearby Kroger stores."}
            </div>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              {stores.map((store) => {
                const isCurrent = store.id === currentStore.id;
                const isStoreFav = favStore?.id === store.id;
                return (
                  <div
                    key={store.id}
                    className={`w-full flex items-start gap-2 px-3 py-2.5 transition-colors ${
                      isCurrent ? "bg-white/80" : "hover:bg-white/60"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isStoreFav) {
                          localStorage.removeItem(FAV_STORE_KEY);
                        } else {
                          localStorage.setItem(FAV_STORE_KEY, JSON.stringify(store));
                        }
                        window.dispatchEvent(new Event("storage"));
                      }}
                      className="mt-0.5 shrink-0 transition-colors"
                      title={isStoreFav ? "Remove default store" : "Set as default store"}
                      style={{ color: isStoreFav ? "#5050C8" : "#9999B5" }}
                    >
                      <Star size={13} fill={isStoreFav ? "currentColor" : "none"} />
                    </button>
                    <button
                      onClick={() => handleSelectStore(store)}
                      className="flex-1 text-left flex items-start gap-2 min-w-0"
                    >
                      <MapPin size={13} className="mt-0.5 shrink-0" style={{ color: "#5050C8" }} />
                      <div className="min-w-0">
                        <p className="text-xs font-body font-medium truncate" style={{ color: "#1C1A3E" }}>
                          {store.name}
                        </p>
                        <p className="text-[10px] font-body" style={{ color: "#54525C" }}>
                          {store.distance != null && `${store.distance} mi`}
                          {store.address && store.distance != null && " · "}
                          {store.address}
                        </p>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Change zip code */}
          <div className="border-t px-3 py-2" style={{ borderColor: "#C8C8F4" }}>
            {editingZip ? (
              <div className="flex gap-1.5">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={newZip}
                  onChange={(e) => setNewZip(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && handleZipSubmit()}
                  placeholder="New zip code"
                  className="flex-1 px-2 py-1 rounded-lg text-xs font-body bg-white border focus:outline-none focus:ring-1"
                  style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
                  autoFocus
                />
                <button
                  onClick={handleZipSubmit}
                  disabled={!/^\d{5}$/.test(newZip.trim())}
                  className="px-2 py-1 rounded-lg text-xs font-body text-primary-foreground disabled:opacity-50"
                  style={{ backgroundColor: "#5050C8" }}
                >
                  Go
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setNewZip("");
                  setEditingZip(true);
                }}
                className="text-xs font-body hover:underline"
                style={{ color: "#5050C8" }}
              >
                Change zip code
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function getFavoriteStore(): StoreInfo | null {
  try {
    const raw = localStorage.getItem(FAV_STORE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
